import { iEmployee } from "../../../cached-data/types"
import { CicoDiffExceptionSettings, ExceptionsState, TkCellData } from "../../../common/types"
import { tContext } from "../types"
import { Dispatch } from "react"
import isEqual from "lodash/isEqual"
import { getFlagEnabled } from "../../../getFlagValue"
import { TkRowData } from "../../../common/types"

/**
 * The `ExceptionsState` sub-fields that configure exception filtering (as opposed to the fetched
 * data used to evaluate them, like `tkSummary`). AG Grid's `isExternalFilterPresent`/
 * `doesExternalFilterPass` are kept as stable class-property references (they must not be
 * redefined on every render, or AG Grid enters an infinite filter-changed loop -- see
 * CustomTable.isExternalFilterPresent's docstring), so AG Grid never notices when the Redux state
 * they read changes. Callers must explicitly call `gridApi.onFilterChanged()` whenever one of
 * these fields changes.
 */
const EXCEPTION_FILTER_FIELDS: (keyof ExceptionsState)[] = ["filterExceptions", "cicoDiffException", "workRulesException"]

/**
 * Determine whether any of the exception filter configuration (as opposed to fetched exception
 * data) changed between two `ExceptionsState` values, so the caller knows whether it needs to
 * force AG Grid to re-run its external filter via `gridApi.onFilterChanged()`.
 *
 * WA-9664: previously nothing called `onFilterChanged()` after a user changed exception filter
 * settings (e.g. via the CICO exception modal's Submit button) or toggled "Apply the filter", so
 * the displayed rows never actually updated even though the underlying Redux state had.
 *
 * @param prevExceptions The `ExceptionsState` before the update
 * @param exceptions The `ExceptionsState` after the update
 * @returns true if any exception filter configuration field differs between the two states
 */
export const didExceptionFiltersChange = (prevExceptions: ExceptionsState | undefined, exceptions: ExceptionsState | undefined): boolean =>
    EXCEPTION_FILTER_FIELDS.some(field => !isEqual(exceptions?.[field], prevExceptions?.[field]))

/**
 * Activate an exception filter
 * @param toggleFn
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const activateException = (toggleFn: () => Record<string, any>) => {
    return (context: tContext) => {
        const dateCols = context.columnApi!.getAllGridColumns()
        context.gridApi!.setFilterModel({
            [dateCols.at(-1)!.getId()]: {},
        })
        return (dispatch: Dispatch<any>): any => dispatch(toggleFn())
    }
}

/**
 * Determine whether the data from a cell constitutes an exception for the CI/CO time
 * @param data Cell data
 * @param exceptionValue The exception value and units
 */
const hoursExceedCico = (data: TkCellData, exceptionValue: CicoDiffExceptionSettings) => {
    const enteredHours = data.cicoTime
    // The meal duration is already baked in to the clocked_in_duration.
    const cicoHours = (data.clocked_in_duration || 0) / 60
    if (!enteredHours) return false
    const value = parseFloat(exceptionValue.value)
    if (exceptionValue.units === "hours") return Math.abs(enteredHours - cicoHours) > value
    else {
        if (!cicoHours || !enteredHours) return true
        if (enteredHours > cicoHours) return Math.abs(1 - cicoHours / enteredHours) > value / 100
        else return Math.abs(1 - enteredHours / cicoHours) > value / 100
    }
}

/**
 * Determine whether a row-group's leaf children add up to nothing but an empty WorkShift
 * placeholder -- e.g. every timekeeping entry was deleted out of a shift but the WorkShift
 * resource itself still exists, leaving a blank row behind.
 *
 * This deliberately looks at each leaf child's own `dataType` rather than
 * `reduceWeeklyViewNodeData`'s aggregate: that reducer only tallies real time from
 * `timekeepingEntries`, `timelineEntryVersions`, and `ewsStartStopTimes`, so a group containing a
 * real `absences` or `employeeEntries` record (but no timekeeping entry) would reduce to all-zero
 * data and be misclassified as empty. Checking dataType directly is correct regardless of which
 * resources the reducer does or doesn't tally.
 * @param leafDataTypes The `dataType` of each of the row-group's leaf children (dummy children excluded)
 * @returns true if every leaf child is a WorkShift record, i.e. there's no real data at all
 */
export const isEmptyWorkShiftRow = (leafDataTypes: string[]): boolean => leafDataTypes.length > 0 && leafDataTypes.every(dataType => dataType === "workShifts")

/**
 * Determine whether a cell constitutes a CI/CO exception
 * @param exceptionValue The value and units of the exception
 * @param cellData The data for the cell
 * @param employee Optional employee object. There are different circumstances depending on where
 * this function gets called. If we have the employee, it's a little simpler as the totals have
 * already been stored there
 * @param isTotalCell Whether the cell is the total cell at the end of the week
 */
export const isCicoException = (exceptionValue: CicoDiffExceptionSettings, cellData?: TkRowData, employee?: iEmployee, isTotalCell?: boolean): boolean => {
    if (isTotalCell) return false
    let hoursExceed = false
    let editedByOther = false
    let anyEdits = false

    if (!employee && cellData) {
        if (!cellData.byDate || !Object.keys(cellData.byDate).length) {
            // Ignore cells with no cico data (if the settings dictate)
            if (exceptionValue.ignoreNoCico && !cellData.clocked_in_duration) return false
            hoursExceed = hoursExceedCico(cellData, exceptionValue)
            editedByOther = cellData.edited_by_other > 0
            anyEdits = cellData.version_count > cellData.item_count
        } else {
            // Ignore cells with no cico data (if the settings dictate)
            if (exceptionValue.ignoreNoCico && !Object.values(cellData.byDate).some(data => data.clocked_in_duration)) return false
            hoursExceed = Object.values(cellData.byDate).some(data => {
                return getFlagEnabled("WA-8941-exceptions-fixes") && exceptionValue.ignoreNoCico && !data.clocked_in_duration ? false : hoursExceedCico(data, exceptionValue)
            })
            editedByOther = Object.values(cellData.byDate).some(data => data.edited_by_other > 0)
            anyEdits = Object.values(cellData.byDate).some(data => data.version_count > data.item_count)
        }
    } else if (employee) {
        const { entered_time_duration, working_duration } = employee
        const value = parseFloat(exceptionValue.value)
        if (exceptionValue.ignoreNoCico && !working_duration) return false
        if (exceptionValue.units === "hours" && Math.abs(employee.cicoDiff || 0) > value * 60) return true
        else if (exceptionValue.units === "percent" && entered_time_duration && working_duration) {
            if (entered_time_duration > working_duration && Math.abs(1 - working_duration / entered_time_duration) > value / 100) hoursExceed = true
            else if (working_duration && Math.abs(1 - entered_time_duration / working_duration) > value / 100) hoursExceed = true
        }
    }

    return hoursExceed || (editedByOther && exceptionValue.flagOthersEdits) || (anyEdits && exceptionValue.flagAnyEdits)
}
